import { paginatorFactory } from '../factories/paginator-factory';
import { templateService } from '../services/template-service';

/**
 * Testimonials list component
 */
export function TestimonialsListComponent() {
  let paginator;
  let ratingItemsContainer;
  let ratingListHeader;
  let sortBySelector;

  const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const sortOptions = [
    { id: 'newest', sortBy: 'timestamp', direction: 'desc' },
    { id: 'oldest', sortBy: 'timestamp', direction: 'asc' },
    { id: 'highest-rated', sortBy: 'rating', direction: 'desc' },
    { id: 'lowest-rated', sortBy: 'rating', direction: 'asc' },
  ];

  let selectedSortOption;

  /**
   * Initializes the testimonials list.
   */
  const init = () => {
    ratingListHeader = document.querySelector('#rating-list-header');
    ratingItemsContainer = document.querySelector('#rating-items-container');

    initPaginator();
    initSortBySelector();
  };

  /**
   * Initializes the paginator.
   */
  const initPaginator = () => {
    paginator = paginatorFactory.create(
      'Testimonials',
      document.querySelector('#rating-items-pagination-container'),
      (pageNumber) => getPage(pageNumber, true)
    );
  };

  /**
   * Initializes the sort by selector.
   */
  const initSortBySelector = () => {
    sortBySelector = document.querySelector('#testimonials-sort-menu');
    sortBySelector.addEventListener('change', (event) => setSortOption(event.target.value));
    setSortOption(sortBySelector.value);
  };

  /**
   * Sets the sort option
   *
   * @param {string} optionId The id of the sort option
   */
  const setSortOption = (optionId) => {
    paginator.clear();
    selectedSortOption = sortOptions.find((option) => option.id === optionId);
    getPage(1);
  };

  /**
   * Gets the page of testimonials
   *
   * @param {number}  page        The page number
   * @param {boolean} returnToTop Whether or not to scroll to the top of the testimonials list
   */
  const getPage = (page, returnToTop = false) => {
    $.ajax({
      url: `${process.env.API_URL}/testimonials/${selectedSortOption.sortBy}/${selectedSortOption.direction}`,
      data: { page: page },
      type: 'GET',
      dataType: 'json',
      success: (response) => handleTestimonialsResponse(response, returnToTop),
      error: handleTestimonialsResponseError,
    });
  };

  /**
   * Handles the testimonials response
   *
   * @param {object}  response    The response object
   * @param {boolean} returnToTop Whether or not to scroll to the top of the testimonials list
   */
  const handleTestimonialsResponse = (response, returnToTop) => {
    setPageFromTestimonials(response.testimonials);
    if (returnToTop) {
      window.scrollBy({ top: ratingItemsContainer.getBoundingClientRect().y - 270, behavior: 'smooth' });
    }
  };

  /**
   * Handles the testimonials response error
   */
  const handleTestimonialsResponseError = () => {
    const errorTextElement = document.createElement('h2');

    errorTextElement.innerHTML = 'Error: Failed to get testimonials';
    errorTextElement.classList.add('has-alert-color');

    ratingItemsContainer.innerHTML = '';
    ratingItemsContainer.appendChild(errorTextElement);

    throw new Error('Failed to get testimonials');
  };

  /**
   * Sets the list of testimonials based on the response
   *
   * @param {object} testimonials The testimonials pagination object
   */
  const setPageFromTestimonials = (testimonials) => {
    ratingItemsContainer.innerHTML = '';
    setHeader(testimonials);
    testimonials.data.forEach((testimonial) => appendNewRatingItem(testimonial));
    paginator.update(testimonials);
  };

  /**
   * Sets the header of the testimonials list
   *
   * @param {object} testimonials The testimonials pagination object
   */
  const setHeader = (testimonials) => {
    ratingListHeader.innerHTML =
      `Page ${testimonials.current_page}:` +
      ` Displaying ${testimonials.to - testimonials.from + 1} of ${testimonials.total} Reviews`;
  };

  /**
   * Appends a new rating item to the testimonials list
   *
   * @param {object} testimonial The testimonial object
   */
  const appendNewRatingItem = (testimonial) => {
    const ratingItem = document.createElement('div');
    setRatingItemFromTestimonial(ratingItem, testimonial);
    ratingItemsContainer.appendChild(ratingItem);
  };

  /**
   * Rounds a number to the specified number of decimals
   *
   * @param {number} value    The value to round
   * @param {number} decimals The number of decimals to round to
   */
  const round = (value, decimals) => {
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
  };

  /**
   * Calculates the overall rating from the testimonial
   *
   * @param {object} testimonial The testimonial object
   */
  const calculateOverallFromTestimonial = (testimonial) => {
    return round((testimonial.privacy_stats + testimonial.speed_stats + testimonial.convenience_stats) / 3, 1);
  };

  /**
   * Sets the rating item from the testimonial
   *
   * @param {HTMLDivElement} ratingItem       The rating item element
   * @param {object}         testimonial      The testimonial object
   * @param {boolean}        feedbackReceived Whether or not feedback was already received for this testimonial
   */
  const setRatingItemFromTestimonial = (ratingItem, testimonial, feedbackReceived = false) => {
    testimonial.overall = calculateOverallFromTestimonial(testimonial).toString();

    ratingItem.innerHTML = getRatingItemHtmlFromTestimonial(testimonial);
    setRatingItemProgressBarsFromTestimonials(ratingItem, testimonial);
    if (feedbackReceived) {
      getHelpfulReviewTextContainerElement(ratingItem).innerHTML = 'Thank you for your feedback!';
      disableHelpfulButtons(ratingItem);
    } else {
      setHelpfulButtonCallbacksOnRatingItem(ratingItem, testimonial);
    }
  };

  /**
   * Disables the helpful buttons on the rating item
   *
   * @param {HTMLDivElement} ratingItem The rating item element
   */
  const disableHelpfulButtons = (ratingItem) => {
    ['helpful-button', 'not-helpful-button'].forEach((className) => {
      ratingItem.querySelector(`.${className}`).classList.add('disabled');
    });
  };

  /**
   * Sets the helpful button callbacks on the rating item
   *
   * @param {HTMLDivElement} ratingItem  The rating item element
   * @param {object}         testimonial The testimonial object
   */
  const setHelpfulButtonCallbacksOnRatingItem = (ratingItem, testimonial) =>
    [
      { className: 'helpful-button', testimonialKey: 'positive_ratings' },
      { className: 'not-helpful-button', testimonialKey: 'negative_ratings' },
    ].forEach(({ className, testimonialKey }) =>
      ratingItem.querySelector(`.${className}`).addEventListener('click', () => {
        testimonial[testimonialKey]++;
        $.ajax({
          url: `${process.env.API_URL}/survey/${testimonial.id}`,
          data: {
            positive_ratings: testimonial.positive_ratings,
            negative_ratings: testimonial.negative_ratings,
          },
          type: 'PUT',
          dataType: 'json',
          success: () => setRatingItemFromTestimonial(ratingItem, testimonial, true),
          error: () => handleRatingResponseError(ratingItem),
        });
      })
    );

  /**
   * Handles the rating response error
   *
   * @param {HTMLDivElement} ratingItem The rating item element
   */
  const handleRatingResponseError = (ratingItem) => {
    const helpfulReviewTextContainerElement = getHelpfulReviewTextContainerElement(ratingItem);

    helpfulReviewTextContainerElement.innerHTML = 'Error: Failed to submit feedback.';
    helpfulReviewTextContainerElement.classList.add('has-alert-color');
    disableHelpfulButtons(ratingItem);

    throw new Error('Error: Failed to submit feedback.');
  };

  /**
   * Gets the helpful review text container element
   *
   * @param {HTMLDivElement} ratingItem The rating item element
   */
  const getHelpfulReviewTextContainerElement = (ratingItem) => {
    return ratingItem.querySelector('.helpful-review-text');
  };

  /**
   * Sets the rating item progress bars from the testimonial
   *
   * @param {HTMLDivElement} ratingItem  The rating item element
   * @param {object}         testimonial The testimonial object
   */
  const setRatingItemProgressBarsFromTestimonials = (ratingItem, testimonial) =>
    [
      { className: 'service-progress-meter', testimonialKey: 'privacy_stats' },
      { className: 'speed-progress-meter', testimonialKey: 'speed_stats' },
      { className: 'convenience-progress-meter', testimonialKey: 'convenience_stats' },
      { className: 'overall-progress-meter', testimonialKey: 'overall' },
    ].forEach(({ className, testimonialKey }) => {
      ratingItem.querySelector(`.${className}`).style.width = `${testimonial[testimonialKey] * 10}%`;
    });

  /**
   * Gets the rating item html from the testimonial
   *
   * @param {object} testimonial The testimonial object
   */
  const getRatingItemHtmlFromTestimonial = (testimonial) =>
    templateService().renderHtmlFromTemplate('rating-item-template', {
      service: testimonial.privacy_stats,
      speed: testimonial.speed_stats,
      convenience: testimonial.convenience_stats,
      overall: testimonial.overall,
      content: testimonial.comment,
      date: parseDate(testimonial.timestamp),
      totalRatings: testimonial.positive_ratings + testimonial.negative_ratings,
      positiveRatings: testimonial.positive_ratings,
    });

  /**
   * Parses the date from the 8601 timestamp
   *
   * @param {string} _8601TimeStamp The 8601 timestamp
   */
  const parseDate = (_8601TimeStamp) => {
    const date = new Date(_8601TimeStamp);

    return `${monthAbbreviations[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  $(() => init());
}

new TestimonialsListComponent();
